/* global gdAlertify */
import React, { useState, useEffect } from "react";
import axiosInstance from "../config/axiosConfig";
import GdTable from "./components/GdTable";
import { formatMoney } from "../utils/utils";
import CartolaPacienteModal from "./components/CartolaPacienteModal";

const CobranzaFacturas = ({ fecha, isPage = false }) => {
    const [resultados, setResultados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [uploadingId, setUploadingId] = useState(null);
    const [verHistorial, setVerHistorial] = useState(null); // { id, nombre, facturas }
    const [loadingHistorial, setLoadingHistorial] = useState(false);
    const [showCartolaModal, setShowCartolaModal] = useState(false);
    const [selectedPacienteCartola, setSelectedPacienteCartola] = useState(null);

    const [fechaFilter, setFechaFilter] = useState("");

    const buscarFacturas = async () => {
        const queryFecha = isPage ? fechaFilter : fecha;
        if (!queryFecha) return;
        setLoading(true);
        try {
            const params = { fecha: queryFecha };
            const response = await axiosInstance.get("/acuerdo-mutuo/cobranza/facturas", { params });
            setResultados(response.data.data || []);
        } catch (error) {
            console.error("Error al buscar facturas:", error);
            gdAlertify.notiError("Error al cargar la vista de facturas");
        }
        setLoading(false);
    };

    useEffect(() => {
        if (!isPage && fecha) {
            buscarFacturas();
        } else if (!isPage) {
            setResultados([]);
        }
    }, [fecha, isPage]);

    const handleFileUpload = (e, row) => {
        const file = e.target.files[0];
        if (!file) return;

        gdAlertify.confirm({
            title: 'Subir Factura',
            message: `¿Está seguro de subir la factura para el paciente ${row.nombre_paciente}?`,
            onConfirm: async () => {
                const queryFecha = isPage ? fechaFilter : fecha;
                const formData = new FormData();
                formData.append("archivo", file);
                formData.append("paciente_id", row.paciente_id);
                formData.append("periodo", queryFecha);
                formData.append("total_cobrar", row.total_cobrar);
                formData.append("total_abonado", row.total_abonado);

                setUploadingId(row.paciente_id);
                try {
                    const resp = await axiosInstance.post("/acuerdo-mutuo/cobranza/facturas/upload", formData, {
                        headers: { "Content-Type": "multipart/form-data" }
                    });
                    if (resp.data && !resp.data.error) {
                        gdAlertify.notiSuccess("Factura subida con éxito");
                        buscarFacturas();
                    } else {
                        gdAlertify.notiError(resp.data.message || "Error al subir factura");
                    }
                } catch (error) {
                    console.error("Error al subir archivo:", error);
                    gdAlertify.notiError("Error al subir factura a S3");
                }
                setUploadingId(null);
            },
            onCancel: () => {
                gdAlertify.notify('Cargada cancelada', 'error');
            }
        });
        e.target.value = null; // reset file input
    };

    const handleHistorial = async (row) => {
        setLoadingHistorial(true);
        try {
            const resp = await axiosInstance.get(`/acuerdo-mutuo/cobranza/facturas/historial/${row.paciente_id}`);
            if (!resp.data.error) {
                setVerHistorial({
                    id: row.paciente_id,
                    nombre: row.nombre_paciente,
                    facturas: resp.data.data
                });
            } else {
                gdAlertify.notiError(resp.data.message || "Error al cargar historial");
            }
        } catch (error) {
            console.error("Error historial:", error);
            gdAlertify.notiError("Error al conectar con el servidor para el historial");
        }
        setLoadingHistorial(false);
    };

    const columns = [
        { title: "RUT", name: "rut" },
        { title: "Paciente", name: "nombre_paciente" },
        { 
            title: "Saldo Actual (Cartola)", 
            name: "saldo_cartola", 
            type: "number",
            render: row => (
                <span className={`fw-bold ${Number(row.saldo_cartola) > 0 ? 'text-danger' : 'text-primary'}`}>
                    {`$${formatMoney(row.saldo_cartola || 0)}`}
                </span>
            )
        },
        { 
            title: "Total a Cobrar", 
            name: "total_cobrar", 
            type: "number",
            render: row => <span className="fw-bold fs-6">{`$${formatMoney(row.total_cobrar)}`}</span>
        },
        { 
            title: "Total Abonado", 
            name: "total_abonado", 
            type: "number",
            render: row => <span className="fw-bold fs-6 text-success">{`$${formatMoney(row.total_abonado)}`}</span>
        },
        { 
            title: "Saldo Pendiente", 
            name: "saldo_pendiente", 
            type: "number",
            render: row => {
                const pendiente = Number(row.total_cobrar) - Number(row.total_abonado);
                const isAbono = pendiente < 0;
                return (
                    <span className={`fw-bold fs-6 ${pendiente > 0 ? "text-danger" : (isAbono ? "text-primary" : "text-success")}`}>
                        {isAbono ? `-$${formatMoney(Math.abs(pendiente))} (A Favor)` : `$${formatMoney(pendiente)}`}
                    </span>
                );
            }
        },
        {
            title: "Estado Pago",
            name: "estado_pago_factura",
            render: row => (
                <span className={`badge ${row.estado_pago_factura === 'Pagado' ? 'bg-success' : 'bg-warning text-dark'}`}>
                    {row.estado_pago_factura}
                </span>
            )
        },
        {
            title: "Apta Factura",
            name: "apta_para_facturar",
            render: row => {
                const pendiente = Number(row.total_cobrar) - Number(row.total_abonado);
                const apta = pendiente <= 0;
                return (
                    <span className={`badge ${apta ? 'bg-success' : 'bg-secondary outline'}`} style={{ minWidth: '80px' }}>
                        {apta ? 'APTA' : 'CON DEUDA'}
                    </span>
                );
            }
        },
        {
            title: "Doc. Factura",
            name: "archivo_factura",
            render: row => (
                <span className={`badge ${row.archivo_factura ? 'bg-info text-white' : 'bg-danger'}`}>
                    {row.archivo_factura ? (
                        <>
                            <i className="bi bi-check-circle-fill me-1"></i> Subida
                        </>
                    ) : (
                        <>
                            <i className="bi bi-x-circle-fill me-1"></i> Sin Subir
                        </>
                    )}
                </span>
            )
        },
        {
            title: "Razón Social",
            name: "fac_razon_social",
            render: row => <span className="small">{row.fac_razon_social}</span>
        },
        {
            title: "RUT Fact.",
            name: "fac_rut",
            render: row => <span className="small">{row.fac_rut}</span>
        },
        {
            title: "Giro",
            name: "fac_giro",
            render: row => <span className="small">{row.fac_giro}</span>
        },
        {
            title: "Dirección",
            name: "fac_direccion",
            render: row => <span className="small">{row.fac_direccion}</span>
        },
        {
            title: "Comuna",
            name: "fac_comuna",
            render: row => <span className="small">{row.fac_comuna}</span>
        },
        {
            title: "Teléfono",
            name: "fac_telefono",
            render: row => <span className="small">{row.fac_telefono}</span>
        },
        {
            title: "Origen Datos",
            name: "usa_tutor",
            render: row => (
                <span className={`badge ${row.usa_tutor ? 'bg-primary' : 'bg-secondary'}`}>
                    {row.usa_tutor ? 'Tutor' : 'Personal'}
                </span>
            )
        },
        {
            title: "Acciones",
            name: "acciones",
            render: row => (
                <div className="dropdown">
                    <button className="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown" aria-expanded="false">
                       <i className="bi bi-gear"></i> Acciones
                    </button>
                    <ul className="dropdown-menu shadow">
                        {row.archivo_factura && (
                            <li>
                                <a className="dropdown-item" href={row.archivo_factura} target="_blank" rel="noopener noreferrer">
                                    <i className="bi bi-file-earmark-pdf text-info"></i> Ver Factura Actual
                                </a>
                            </li>
                        )}
                        <li>
                            <label className="dropdown-item mb-0" style={{ cursor: 'pointer' }}>
                                <i className="bi bi-upload text-primary"></i> Subir Factura
                                <input 
                                    type="file" 
                                    accept=".pdf,.png,.jpg,.jpeg" 
                                    style={{ display: "none" }} 
                                    onChange={(e) => handleFileUpload(e, row)}
                                    disabled={uploadingId === row.paciente_id}
                                />
                            </label>
                        </li>
                        <li>
                            <button className="dropdown-item" onClick={() => {
                                setSelectedPacienteCartola(row);
                                setShowCartolaModal(true);
                            }}>
                                <i className="bi bi-journal-text text-warning"></i> Ver Cartola
                            </button>
                        </li>
                    </ul>
                </div>
            )
        }
    ];

    return (
        <div className="card mt-2 border-top-0 rounded-top-0">
            <div className="card-body">
                {isPage && (
                    <div className="row mb-3">
                        <div className="col-md-3">
                            <label className="form-label">Mes</label>
                            <input
                                type="month"
                                className="form-control"
                                value={fechaFilter}
                                onChange={e => setFechaFilter(e.target.value)}
                            />
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <button className="btn btn-primary" onClick={buscarFacturas} disabled={!fechaFilter || loading}>
                                Buscar
                            </button>
                        </div>
                    </div>
                )}
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5 className="mb-0 text-muted">Facturas y Saldos {(isPage ? fechaFilter : fecha) ? `(${(isPage ? fechaFilter : fecha)})` : ""}</h5>
                    {!isPage && (
                        <button className="btn btn-sm btn-outline-primary" onClick={buscarFacturas} disabled={!fecha || loading}>
                             <i className="bi bi-arrow-clockwise"></i> Actualizar
                        </button>
                    )}
                </div>
                {(isPage ? fechaFilter : fecha) ? (
                    <GdTable
                        columns={columns}
                        data={resultados}
                        stickyHeader={true}
                        height={isPage ? 700 : 600}
                        headerHeight={50}
                        showCsvExport={true}
                    />
                ) : (
                    <div className="alert alert-info">
                        Seleccione un mes para consultar facturas.
                    </div>
                )}
            </div>

            {/* Modal de Historial */}
            {verHistorial && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header bg-light">
                                <h5 className="modal-title">Historial de Facturas: <span className="text-primary">{verHistorial.nombre}</span></h5>
                                <button type="button" className="btn-close" onClick={() => setVerHistorial(null)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="table-responsive" style={{ maxHeight: '400px' }}>
                                    <table className="table table-hover align-middle">
                                        <thead className="table-light sticky-top">
                                            <tr>
                                                <th>Periodo</th>
                                                <th>Monto Cobrado</th>
                                                <th>Abonado</th>
                                                <th>Estado</th>
                                                <th>Archivo</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {verHistorial.facturas.length > 0 ? verHistorial.facturas.map((f, i) => (
                                                <tr key={i}>
                                                    <td>{f.periodo}</td>
                                                    <td>${formatMoney(f.total_cobrar)}</td>
                                                    <td>${formatMoney(f.total_abonado)}</td>
                                                    <td>
                                                        <span className={`badge ${f.estado_pago === 'Pagado' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                            {f.estado_pago}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {f.archivo_path && (
                                                            <a href={f.archivo_path} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-info text-white">
                                                                <i className="bi bi-eye"></i> Ver
                                                            </a>
                                                        )}
                                                    </td>
                                                </tr>
                                            )) : (
                                                <tr><td colSpan="5" className="text-center py-3">No hay facturas registradas anteriormente.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setVerHistorial(null)}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {loadingHistorial && (
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 9999 }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            )}

            <CartolaPacienteModal
                show={showCartolaModal}
                handleClose={() => setShowCartolaModal(false)}
                paciente={selectedPacienteCartola}
            />
        </div>
    );
};

export default CobranzaFacturas;
